import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import './SharePost.css'; // Reuse the same styles
import AppStoreButton from './AppStoreButton';

export default function ShareUser() {
  const { userId } = useParams();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Fetch user data from Supabase Edge Function
    const fetchUser = async () => {
      try {
        setLoading(true);
        
        // Validate userId is a valid UUID format
        const uuidRegex = /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/i;
        if (!uuidRegex.test(userId)) {
          throw new Error('Invalid user ID format');
        }
        
        const SUPABASE_FUNCTIONS_URL = process.env.REACT_APP_SUPABASE_FUNCTIONS_URL || 'https://lyyjnlgqwfskbvwqqlrh.functions.supabase.co';
        const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || '';
        
        if (!SUPABASE_ANON_KEY) {
          throw new Error('API configuration error');
        }
        
        // Call the Supabase Edge Function that returns user data with full signed URL
        const response = await fetch(
          `${SUPABASE_FUNCTIONS_URL}/get-user-preview?id=${encodeURIComponent(userId)}`,
          {
            headers: {
              'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
              'Content-Type': 'application/json',
            }
          }
        );
        
        if (!response.ok) {
          // Don't expose HTTP status codes to users
          throw new Error('Failed to load user profile');
        }
        
        const user = await response.json();
        
        if (user && user.id) {
          // The Edge Function returns the user with full signed URL already included
          // Only log in development
          if (process.env.NODE_ENV === 'development') {
            console.log('User data from Edge Function:', user);
          }
          
          setUserData({
            id: user.id,
            username: user.username || 'Unknown',
            avatar_url: user.avatar_url || '',
            expires_in_seconds: user.expires_in_seconds || 3600
          });
        } else {
          setError('User not found. The profile link may have expired or been deleted.');
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching user:', err);
        setError('Failed to load user profile. The link may have expired or been deleted.');
        setLoading(false);
      }
    };

    if (userId) {
      fetchUser();
    } else {
      setError('Invalid user link');
      setLoading(false);
    }
  }, [userId]);

  // Update meta tags for social sharing
  useEffect(() => {
    if (userData) {
      const updateMetaTag = (property, content) => {
        let element = document.querySelector(`meta[property="${property}"]`);
        if (!element) {
          element = document.createElement('meta');
          element.setAttribute('property', property);
          document.head.appendChild(element);
        }
        element.setAttribute('content', content);
      };

      const updateNameTag = (name, content) => {
        let element = document.querySelector(`meta[name="${name}"]`);
        if (!element) {
          element = document.createElement('meta');
          element.setAttribute('name', name);
          document.head.appendChild(element);
        }
        element.setAttribute('content', content);
      };

      const userUrl = `https://www.yuppy.nyc/users/${userId}`;
      const title = `Check out ${userData.username} on Yuppy`;
      const description = `View ${userData.username}'s profile and join NYC's top dog community.`;

      // Update title
      document.title = title;

      // Update Open Graph tags
      updateMetaTag('og:title', title);
      updateMetaTag('og:description', description);
      if (userData.avatar_url) {
        updateMetaTag('og:image', userData.avatar_url);
      }
      updateMetaTag('og:url', userUrl);
      updateMetaTag('og:type', 'profile');

      // Update Twitter tags
      updateNameTag('twitter:card', 'summary_large_image');
      updateNameTag('twitter:title', title);
      updateNameTag('twitter:description', description);
      if (userData.avatar_url) {
        updateNameTag('twitter:image', userData.avatar_url);
      }
    }
  }, [userData, userId]);

  const handleOpenApp = () => {
    // Try to open the app using universal link
    const appUrl = `yuppy://users/${userId}`;
    const appStoreUrl = 'https://apps.apple.com/us/app/yuppy-nyc/id6749724524';
    
    // Try to open app first
    window.location.href = appUrl;
    
    // Fallback to App Store after a short delay if app doesn't open
    setTimeout(() => {
      window.location.href = appStoreUrl;
    }, 500);
  };

  if (loading) {
    return (
      <div className="share-post-container">
        <div className="share-post-loading">Loading...</div>
      </div>
    );
  }

  if (error || !userData) {
    return (
      <div className="share-post-container">
        <div className="share-post-error">
          <h2>Profile Not Found</h2>
          <p>{error || 'This profile could not be found.'}</p>
          <AppStoreButton />
        </div>
      </div>
    );
  }

  return (
    <div className="share-post-container">
      {/* Mobile-only header */}
      <div className="share-post-mobile-header">
        <div className="share-post-header-logo">
          <img 
            src={require('./assets/images/fixed-logo-1024.png')} 
            alt="Yuppy logo" 
            className="share-post-logo-icon"
          />
          <span className="share-post-header-text">yuppy</span>
        </div>
      </div>
      <div className="share-post-card">
        <div className="share-post-image-container">
          {userData.avatar_url ? (
            <img 
              src={userData.avatar_url} 
              alt={`${userData.username}'s profile on Yuppy`}
              className="share-post-image"
              onError={(e) => {
                // Only log in development
                if (process.env.NODE_ENV === 'development') {
                  console.error('Image failed to load');
                }
                e.target.src = require('./assets/images/fixed-logo-1024.png');
              }}
              onLoad={() => {
                // Only log in development
                if (process.env.NODE_ENV === 'development') {
                  console.log('Image loaded successfully');
                }
              }}
            />
          ) : (
            <div className="share-post-image-placeholder">
              <img 
                src={require('./assets/images/fixed-logo-1024.png')} 
                alt="Yuppy logo"
                className="share-post-image"
              />
            </div>
          )}
        </div>
        <div className="share-post-content">
          <h2 className="share-post-title">
            Check out {userData.username} on Yuppy
          </h2>
          <p className="share-post-description">
            View {userData.username}'s profile and join NYC's top dog community.
          </p>
          <div className="share-post-cta">
            <button 
              className="share-post-open-button"
              onClick={handleOpenApp}
            >
              Open Yuppy
            </button>
            <a 
              className="share-post-download-button"
              href="https://apps.apple.com/us/app/yuppy-nyc/id6749724524"
              target="_blank"
              rel="noopener noreferrer"
            >
              Download Yuppy
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

